<template>
  <div class="config-system">
    <!-- 使用 el-container 包裹整个布局 -->
    <el-container direction="vertical">
      <AdminHeader @logout="handleLogout" />

      <!-- 主内容区 -->
      <el-container>
        <AdminSidebar />

        <!-- 配置内容区 -->
        <el-main>
        <!-- 配置表格：展示已有门店的欢迎语配置 -->
        <el-card shadow="always" header="门店欢迎语配置列表">
          <div style="margin-bottom: 15px;">
            <el-button type="primary" @click="handleAdd">新增配置</el-button>
          </div>
          <div class="table-container">
            <el-table :data="configList" border style="width: 100%" height="400">
              <el-table-column prop="id" label="ID" width="80"></el-table-column>
              <el-table-column prop="storeId" label="门店编号" width="100"></el-table-column>
              <el-table-column prop="storeName" label="门店名称" width="120"></el-table-column>
              <el-table-column prop="welcomeText" label="欢迎语" width="200"></el-table-column>
              <el-table-column label="文字样式" width="220">
                <template #default="scope">
                  <div class="style-info">
                    <span>大小: {{ formatFontSize(scope.row.fontSize) }}</span><br>
                    <span>颜色: {{ formatTextColor(scope.row) }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="展示方式" width="100">
                <template #default="scope">
                  <span>{{ (scope.row.isFullscreen === 1 || scope.row.isFullscreen === '1') ? '全屏' : '半屏' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="isEnabled" label="是否启用" width="100">
                <template #default="scope">
                  <el-switch v-model="scope.row.isEnabled" @change="updateEnabledStatus(scope.row)"></el-switch>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="180">
                <template #default="scope">
                  <el-button type="text" @click="editConfig(scope.row)">编辑</el-button>
                  <el-button type="text" @click="deleteConfig(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-card>
        
        <!-- 弹出表单组件 -->
        <WelcomeConfigForm
          :visible.sync="dialogVisible"
          :form-data="currentFormData"
          :is-edit="isEdit"
          @submit="handleFormSubmit"
          @close="handleFormClose"
        />
      </el-main>
    </el-container>
    </el-container>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config';
import WelcomeConfigForm from '@/components/WelcomeConfigForm.vue';
import AdminHeader from '@/components/AdminHeader.vue';
import AdminSidebar from '@/components/AdminSidebar.vue';
import { clearAuthInfo, getUserInfo, getUserStoreIds } from '@/utils/auth';
import { parseApiResponse, handleApiError, camelToSnake, snakeToCamel } from '@/utils/apiResponse';

export default {
  components: {
    WelcomeConfigForm,
    AdminHeader,
    AdminSidebar
  },
  data() {
    return {
      configList: [], // 配置列表
      dialogVisible: false, // 弹出框显示状态
      currentFormData: null, // 当前表单数据
      isEdit: false, // 是否为编辑状态
      editId: '' // 编辑的配置ID
    };
  },
  created() {
    this.fetchConfigList(); // 初始化时获取配置列表
  },
  methods: {
    // 获取配置列表
    async fetchConfigList() {
      try {
        // 配置请求头
        const axiosConfig = {
          headers: {
            'Content-Type': 'application/json'
          }
        };

        const response = await axios.get(config.getApiUrl(config.apiEndpoints.welcomeConfig.list), axiosConfig);
        
        // 使用统一的响应解析函数处理 ApiResponse 格式
        const result = parseApiResponse(response);
        
        if (result.success && result.data) {
          // 后端返回的数据使用下划线命名，转换为驼峰命名供前端使用
          const configData = Array.isArray(result.data) ? result.data : [];
          
          // 将后端返回的下划线字段名转换为驼峰命名，并处理数据类型
          this.configList = configData.map(item => {
            // 先转换为驼峰命名
            const camelItem = snakeToCamel(item);
            
            return {
              ...camelItem,
              // 处理 isEnabled：响应中是 Boolean，但需要兼容旧格式
              isEnabled: camelItem.isEnabled === true || camelItem.isEnabled === 1 || camelItem.isEnabled === '1',
              // 处理 fontSize
              fontSize: typeof camelItem.fontSize === 'number' ? camelItem.fontSize : parseInt(camelItem.fontSize) || 28,
              // 处理 textColor/fontColor
              textColor: camelItem.textColor || camelItem.fontColor || camelItem.font_color || '#000000',
              backgroundColor: camelItem.backgroundColor || '',
              // 处理 isFullscreen
              isFullscreen: camelItem.isFullscreen !== undefined ? (typeof camelItem.isFullscreen === 'number' ? camelItem.isFullscreen : parseInt(camelItem.isFullscreen) || 0) : 0,
              // 保留 videos 字段，以便在编辑表单中使用
              videos: camelItem.videos || []
            };
          });
        } else {
          this.$message.error(result.message || '获取配置列表失败');
          this.configList = [];
        }
      } catch (error) {
        const errorResult = handleApiError(error);
        this.$message.error(errorResult.message || '获取配置列表异常');
        this.configList = [];
      }
    },
    // 新增配置
    handleAdd() {
      this.isEdit = false;
      this.editId = '';
      this.currentFormData = null;
      this.dialogVisible = true;
    },
    // 编辑配置
    editConfig(row) {
      this.isEdit = true;
      this.editId = row.id;
      this.currentFormData = { ...row };
      this.dialogVisible = true;
    },
    // 处理表单提交
    async handleFormSubmit(formData) {
      await this.saveConfig(formData);
    },
    // 处理表单关闭
    handleFormClose() {
      this.currentFormData = null;
      this.isEdit = false;
      this.editId = '';
    },
    // 删除配置
    deleteConfig(row) {
      this.$confirm('确定删除该配置吗？', '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          // 配置请求头
          const axiosConfig = {
            headers: {
              'Content-Type': 'application/json'
            }
          };

          const response = await axios.delete(config.getApiUrl(config.apiEndpoints.welcomeConfig.delete, row.id), axiosConfig);
          
          // 使用统一的响应解析函数处理 ApiResponse 格式
          const result = parseApiResponse(response);
          
          if (result.success) {
            this.$message.success(result.message || '删除成功');
            this.fetchConfigList(); // 重新获取列表
          } else {
            this.$message.error(result.message || '删除失败');
          }
        } catch (error) {
          const errorResult = handleApiError(error);
          this.$message.error(errorResult.message || '删除配置异常');
        }
      });
    },
    // 保存配置
    async saveConfig(formData) {
      // 表单验证
      if (!formData.storeId || !formData.storeName || !formData.welcomeText) {
        this.$message.error('请填写完整的门店信息和欢迎语内容');
        return;
      }

      // 门店权限校验（前端兜底）：只允许对“当前登录用户关联门店”新增/编辑欢迎语
      // 注意：最终权限以服务端为准；这里的校验主要用于避免误操作（例如：登录A门店却填写B门店ID）。
      const allowedStoreIds = (getUserStoreIds() || []).map(id => String(id));
      if (allowedStoreIds.length === 0) {
        // 获取不到门店权限时直接阻止提交，避免越权/脏数据；如需放开，可改为仅提示不拦截。
        this.$message.warning('未获取到可操作的门店权限，请重新登录后重试');
        return;
      }
      if (!allowedStoreIds.includes(String(formData.storeId))) {
        this.$message.warning('请新增相关门店欢迎语');
        return;
      }

      try {
        // 构建要发送的数据，使用下划线命名
        const submitData = {
          store_id: formData.storeId,                    // 字符串类型
          store_name: formData.storeName,                // 字符串类型
          welcome_text: formData.welcomeText,            // 字符串类型
          font_size: parseInt(formData.fontSize) || 16,  // 数字类型，从字符串转换
          font_color: formData.textColor || '#000000',   // 字体颜色
          is_enabled: formData.isEnabled ? 1 : 0,        // 数字类型：1启用，0禁用
          is_fullscreen: formData.isFullscreen !== undefined ? formData.isFullscreen : 0  // 0=半屏，1=全屏
        };

        // console.log('准备保存的配置数据:', formData);

        // 配置请求头
        const axiosConfig = {
          headers: {
            'Content-Type': 'application/json'
          }
        };

        let response;
        let apiUrl;
        
        if (this.isEdit) {
          // 编辑模式：更新现有配置
          apiUrl = config.getApiUrl(config.apiEndpoints.welcomeConfig.update, this.editId);
          // console.log('更新配置 API URL:', apiUrl);
          response = await axios.put(apiUrl, submitData, axiosConfig);
        } else {
          // 新增模式：创建新配置
          apiUrl = config.getApiUrl(config.apiEndpoints.welcomeConfig.create);
          // console.log('创建配置 API URL:', apiUrl);
          response = await axios.post(apiUrl, submitData, axiosConfig);
        }
        
        // console.log('API响应:', response);
        
        // 使用统一的响应解析函数处理 ApiResponse 格式
        const result = parseApiResponse(response);
        
        if (result.success) {
          this.$message.success(result.message || (this.isEdit ? '更新成功' : '保存成功'));
          this.fetchConfigList(); // 重新获取列表
        } else {
          this.$message.error(result.message || '操作失败');
        }
        
      } catch (error) {
        const errorResult = handleApiError(error);
        this.$message.error(errorResult.message || '保存配置异常');
      }
    },
    // 更新启用状态
    async updateEnabledStatus(row) {
      try {
        // 配置请求头
        const axiosConfig = {
          headers: {
            'Content-Type': 'application/json'
          }
        };
        
        // 使用下划线字段名
        const response = await axios.put(
          config.getApiUrl(config.apiEndpoints.welcomeConfig.toggleEnabled, row.id), 
          {
            is_enabled: row.isEnabled ? 1 : 0  // 布尔值转换为数值，使用下划线字段名
          }, 
          axiosConfig
        );

        // 特殊接口：PUT /api/store-welcome-config/{id}/enabled 直接返回字符串
        // 不使用 ApiResponse 包装，直接读取 response.data
        if (response.status === 200) {
          // 直接读取响应体字符串
          const message = typeof response.data === 'string' ? response.data : '状态更新成功';
          this.$message.success(message);
        } else {
          this.$message.error('请求失败，状态码：' + response.status);
          row.isEnabled = !row.isEnabled;
        }
      } catch (error) {
        const errorResult = handleApiError(error);
        this.$message.error(errorResult.message || '状态更新异常');
        
        // 如果更新失败，恢复原状态
        row.isEnabled = !row.isEnabled;
      }
    },
    // 获取表格中样式显示的方法
    getDisplayStyle(row) {
      const size = typeof row.fontSize === 'number' ? row.fontSize : parseInt(row.fontSize) || 16;
      return {
        fontSize: size + 'px',
        color: row.textColor || row.fontColor || row.font_color || '#000000',
        backgroundColor: row.backgroundColor || 'transparent',
        padding: '5px 10px',
        border: '1px solid #eee',
        borderRadius: '3px',
        display: 'inline-block',
        minWidth: '80px',
        minHeight: Math.max(60, size * 2) + 'px',
        lineHeight: Math.max(60, size * 2) + 'px',
        textAlign: 'center'
      };
    },
    formatFontSize(value) {
      const num = typeof value === 'number' ? value : parseInt(value);
      return (num && !isNaN(num)) ? num + 'px' : (value || '16px');
    },
    formatTextColor(row) {
      return (row && (row.textColor || row.fontColor || row.font_color)) || '#000000';
    },
    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清除登录信息
        clearAuthInfo();
        console.log('已清除登录信息，跳转到登录页');
        
        // 显示退出成功消息
        this.$message.success('已退出登录');
        
        // 跳转到登录页
        this.$router.push('/login');
      }).catch(() => {
        // 用户取消，不做任何操作
      });
    }
  }
};
</script>

<style scoped>
.config-system {
  height: 100vh;
  overflow: hidden;
}

.el-container {
  height: 100%;
}

.header-container {
  background-color: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
}

.el-header h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
}

.el-aside {
  background-color: #f5f7fa;
  flex-shrink: 0;
}

.el-main {
  padding: 20px;
  overflow-y: auto;
  height: calc(100vh - 60px); /* 减去header高度 */
}


.style-info {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.style-info span {
  display: inline-block;
  margin-right: 10px;
}

/* 表格样式优化 */
.el-table .cell {
  padding: 8px;
}

/* 颜色选择器样式 */
.el-color-picker {
  vertical-align: middle;
}

/* 表格容器滚动条样式 */
.table-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

/* 自定义滚动条样式 - 与现有UI风格保持一致 */
.table-container :deep(.el-table__body-wrapper) {
  /* Webkit浏览器滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.table-container :deep(.el-table__body-wrapper::-webkit-scrollbar) {
  width: 8px;
  height: 8px;
}

.table-container :deep(.el-table__body-wrapper::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container :deep(.el-table__body-wrapper::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.table-container :deep(.el-table__body-wrapper::-webkit-scrollbar-thumb:hover) {
  background: #409eff;
}

.table-container :deep(.el-table__body-wrapper::-webkit-scrollbar-corner) {
  background: #f1f1f1;
}

/* 移动端触摸滚动优化 */
.table-container :deep(.el-table__body-wrapper) {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* 表格行悬停效果优化 */
.table-container :deep(.el-table__row:hover) {
  background-color: #f5f7fa;
  transition: background-color 0.2s ease;
}

/* 表格边框圆角优化 */
.table-container :deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

.table-container :deep(.el-table th) {
  background-color: #fafafa;
  border-bottom: 1px solid #ebeef5;
}

/* 响应式设计 - 移动端适配 */
@media (max-width: 768px) {
  .table-container :deep(.el-table__body-wrapper::-webkit-scrollbar) {
    width: 6px;
    height: 6px;
  }
  
  .table-container :deep(.el-table__body-wrapper) {
    scrollbar-width: thin;
  }
}

/* 高分辨率屏幕适配 */
@media (min-resolution: 2dppx) {
  .table-container :deep(.el-table__body-wrapper::-webkit-scrollbar) {
    width: 10px;
    height: 10px;
  }
}
</style>
